import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const DailyChart = ({ chats }) => {
  const dailyData = useMemo(() => {
    const days = {};
    const currentDate = new Date();
    
    // Inicializar últimos 15 días
    for (let i = 14; i >= 0; i--) {
      const date = new Date(currentDate);
      date.setDate(currentDate.getDate() - i);
      const key = date.toISOString().split('T')[0]; // YYYY-MM-DD
      days[key] = { sent: 0, received: 0 };
    }

    // Contar mensajes por día
    chats.forEach(chat => {
      if (chat.messages) {
        chat.messages.forEach(message => {
          const messageDate = message.timestamp?.toDate ? message.timestamp.toDate() : new Date(message.timestamp);
          const key = messageDate.toISOString().split('T')[0];
          
          if (days[key]) {
            if (message.type === 'sent') {
              days[key].sent++;
            } else {
              days[key].received++;
            }
          }
        });
      }
    });

    return days;
  }, [chats]);

  const data = {
    labels: Object.keys(dailyData).map(key => {
      const date = new Date(key);
      return date.toLocaleDateString('es-ES', { 
        month: 'short', 
        day: 'numeric' 
      });
    }),
    datasets: [
      {
        label: 'Mensajes Enviados',
        data: Object.values(dailyData).map(day => day.sent),
        borderColor: 'rgba(54, 162, 235, 1)',
        backgroundColor: 'rgba(54, 162, 235, 0.1)',
        tension: 0.4,
        fill: true,
      },
      {
        label: 'Mensajes Recibidos',
        data: Object.values(dailyData).map(day => day.received),
        borderColor: 'rgba(255, 99, 132, 1)',
        backgroundColor: 'rgba(255, 99, 132, 0.1)',
        tension: 0.4,
        fill: true,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 10,
        bottom: 10,
        left: 10,
        right: 10,
      },
    },
    plugins: {
      legend: {
        position: 'top',
        align: 'start',
        labels: {
          usePointStyle: true,
          pointStyle: 'circle',
          padding: 20,
          font: {
            size: 12,
            weight: '500',
          },
        },
      },
      title: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: 'rgba(255, 255, 255, 0.2)',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
        titleFont: {
          size: 14,
          weight: 'bold',
        },
        bodyFont: {
          size: 13,
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.05)',
          borderDash: [2, 2],
        },
        ticks: {
          font: {
            size: 11,
            weight: '500',
          },
          color: '#666',
          maxRotation: 45,
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.05)',
          borderDash: [2, 2],
        },
        ticks: {
          precision: 0,
          font: {
            size: 11,
            weight: '500',
          },
          color: '#666',
        },
      },
    },
    interaction: {
      mode: 'index',
      intersect: false,
    },
    hover: {
      mode: 'nearest',
      intersect: true,
    },
    elements: {
      point: {
        radius: 4,
        hoverRadius: 6,
        borderWidth: 2,
      },
      line: {
        borderWidth: 3,
      },
    },
  };

  return <Line data={data} options={options} />;
};

export default DailyChart;
